import type { Scene } from '../components/Scenes';

export const UNLINKED_SCENES_CONTAINER_ID = '__unlinked_scenes__';

export interface OrderedChapterRef {
  id?: string;
  order?: number;
}

export function isUnlinkedScenesContainer(id: string): boolean {
  return id === UNLINKED_SCENES_CONTAINER_ID;
}

export function buildSceneOrderFromChapterLists(
  chapters: OrderedChapterRef[],
  scenesByChapter: Map<string, Scene[]>,
  allScenes: Scene[],
  orderDocumentId?: string,
): Scene[] {
  const sortedChapters = [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const scenesById = new Map(allScenes.map((scene) => [scene.id, scene]));
  const placedIds = new Set<string>();
  const linkedOrdered: Scene[] = [];
  const isOrderable = (scene: Scene): boolean =>
    !orderDocumentId || !scene.documentId || scene.documentId === orderDocumentId;

  for (const chapter of sortedChapters) {
    if (!chapter.id) continue;
    for (const scene of scenesByChapter.get(chapter.id) ?? []) {
      if (placedIds.has(scene.id) || !isOrderable(scene)) continue;
      linkedOrdered.push(scenesById.get(scene.id) ?? scene);
      placedIds.add(scene.id);
    }
  }

  const orderPool = linkedOrdered
    .map((scene) => scene.order ?? 0)
    .sort((a, b) => a - b);

  const orderById = new Map(
    linkedOrdered.map((scene, index) => [scene.id, orderPool[index]]),
  );

  return allScenes
    .map((scene) => ({
      ...scene,
      order: orderById.get(scene.id) ?? scene.order,
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function scenesByChapterToRecord(
  chapters: OrderedChapterRef[],
  scenesByChapter: Map<string, Scene[]>,
): Record<string, Scene[]> {
  const sortedChapters = [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const record: Record<string, Scene[]> = {};
  const placedIds = new Set<string>();

  for (const chapter of sortedChapters) {
    if (!chapter.id) continue;
    const scenes: Scene[] = [];
    for (const scene of scenesByChapter.get(chapter.id) ?? []) {
      if (placedIds.has(scene.id)) continue;
      placedIds.add(scene.id);
      scenes.push(scene);
    }
    record[chapter.id] = scenes;
  }

  return record;
}

export function scenesByChapterToRecordWithUnlinked(
  chapters: OrderedChapterRef[],
  scenesByChapter: Map<string, Scene[]>,
  unlinkedScenes: Scene[],
): Record<string, Scene[]> {
  const linkedIds = new Set<string>();
  for (const scenes of scenesByChapter.values()) {
    for (const scene of scenes) {
      linkedIds.add(scene.id);
    }
  }

  return {
    ...scenesByChapterToRecord(chapters, scenesByChapter),
    [UNLINKED_SCENES_CONTAINER_ID]: unlinkedScenes.filter((scene) => !linkedIds.has(scene.id)),
  };
}

export function recordToScenesByChapter(record: Record<string, Scene[]>): Map<string, Scene[]> {
  const map = new Map<string, Scene[]>();
  for (const [chapterId, scenes] of Object.entries(record)) {
    if (isUnlinkedScenesContainer(chapterId)) continue;
    map.set(chapterId, scenes);
  }
  return map;
}

export function findChapterContainerId(
  items: Record<string, Scene[]>,
  id: string,
): string | undefined {
  if (id in items) return id;
  return Object.keys(items).find((chapterId) =>
    items[chapterId].some((scene) => scene.id === id),
  );
}
